import { PayrollSession } from '../types/PayrollTypes';
import { createFamilyFormData } from '../domain/tax/types';

/**
 * `familyData` を持たない旧セッション（旧 localStorage / 旧 Google Drive バックアップ）に
 * 既定値を補完する純関数。
 *
 * 既存フィールドは変更せず、`familyData` が未設定のときだけ `createFamilyFormData()` を
 * 追加する（設定済みなら同一オブジェクトを返す）。後方互換のため、読み込み経路で必ず通す。
 */
export function ensureFamilyData(session: PayrollSession): PayrollSession {
  if (session.familyData) {
    return session;
  }
  return { ...session, familyData: createFamilyFormData() };
}

export class PayrollStorage {
  private static readonly STORAGE_KEY = 'payroll_session';
  private static saveTimeout: NodeJS.Timeout | null = null;
  
  static saveSession(session: PayrollSession): void {
    try {
      const updatedSession = {
        ...session,
        lastUpdated: new Date().toISOString()
      };
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(updatedSession));
    } catch (error) {
      console.error('セッション保存エラー:', error);
    }
  }
  
  static loadSession(): PayrollSession | null {
    try {
      const data = localStorage.getItem(this.STORAGE_KEY);
      if (!data) return null;
      
      const session = JSON.parse(data) as PayrollSession;
      
      // データの妥当性チェック
      if (!session.monthlyData) {
        console.warn('無効なセッションデータを検出、削除します');
        this.clearSession();
        return null;
      }
      
      // 読み込み経路では必ず familyData を補完する（冪等）。
      return ensureFamilyData(session);
    } catch (error) {
      console.error('セッション読み込みエラー:', error);
      this.clearSession();
      return null;
    }
  }
  
  static clearSession(): void {
    try {
      localStorage.removeItem(this.STORAGE_KEY);
    } catch (error) {
      console.error('セッション削除エラー:', error);
    }
  }
  
  // デバウンス処理付きの自動保存
  static autoSave(session: PayrollSession, delay: number = 1000): void {
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout);
    }
    
    this.saveTimeout = setTimeout(() => {
      this.saveSession(session);
    }, delay);
  }
  
  // セッションが存在するかチェック
  static hasSession(): boolean {
    return localStorage.getItem(this.STORAGE_KEY) !== null;
  }
  
  // ストレージサイズをチェック（オプション）
  static getStorageSize(): string {
    const data = localStorage.getItem(this.STORAGE_KEY);
    if (!data) return '0 KB';
    
    const sizeInBytes = new Blob([data]).size;
    const sizeInKB = Math.round(sizeInBytes / 1024 * 100) / 100;
    return `${sizeInKB} KB`;
  }
}